import { useLocation } from 'react-router-dom';
import { Bars3Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
// Story 2-147wg: NOT resolved like the two constant-only upward imports
// below — `ThemeToggle.tsx` is registered `composite` (a real, stateful
// dark/light toggle component TopBar renders, not a pure constant this
// file could extract to a lower layer). This is the SAME class of
// legitimate shell-composing-a-real-composite exception
// `UPWARD_IMPORT_ALLOWLIST`'s one existing entry already recognizes
// (`AppShell.tsx` -> `AgentPanel.tsx`) — but adding a second allowlist
// entry is exactly what this story's own Mandatory Migration Rules forbid
// ("zero new lint-debt allowlist entries"), and reclassifying
// `ThemeToggle.tsx`'s registry LAYER is a bigger call than this file's own
// partition authorizes. Left as a genuine, named residual rather than
// gamed or silently dropped — see SESSION-HANDOFF.md for the honest count.
import { ThemeToggle } from './ThemeToggle';
import { Breadcrumbs, Cluster, IconButton } from '../ui';
import { useCapabilities } from '../../lib/capabilities';
import { navRegistry, settingsRegistry, resolveBreadcrumbs } from '../../lib/registries';
import { moduleNavEntries, moduleSettingsEntries } from '../../lib/host';
import { OPEN_SEARCH_EVENT } from '../../lib/searchEvents';
import { AgentStatusTrigger } from './AgentStatusTrigger';

// Story 2-133: color comes from `--agent-status` (tokens.css) via the
// `.agent-status-dot` class + `data-agent-status` attribute below — NOT a
// hardcoded Tailwind class map. This used to be a
// `Record<AgentStatus, string>` of `bg-primary`/`bg-amber-500`/etc, an
// independently-maintained color choice from Sidebar.tsx's own
// `.shell-agent-glow` (2-132), which reads the exact same token — two
// hand-picked palettes for one real-world state, guaranteed to drift.
// `animate-pulse` (motion, not color) stays a plain conditional class.

interface TopBarProps {
  onToggleSidebar: () => void;
}

export function TopBar({ onToggleSidebar }: TopBarProps): JSX.Element {
  const location = useLocation();
  const caps = useCapabilities();
  const { featureState } = caps;
  // The assistant is itself a flagged feature (P02/P18 §2) — `AppShell` only
  // mounts `AgentPanel` when this is on. Mirror that gate here: a status-pill
  // trigger that opens nothing (no listener ever mounted) is a dead
  // affordance, the same class of bug 2-107 removed the fake notifications
  // bell for.
  const aiEnabled = featureState('agents.core').state === 'on';
  // Breadcrumb trail (story 2-104, moved here from AppShell by story 2-129 —
  // see that story's TopBar/AppShell "reconciled to one owner" note): the
  // same nav + settings registries (plus module contributions) that drive
  // the sidebar's active state, resolved against the current path and this
  // principal's capability document. TopBar is now the sole owner of this
  // computation and its render; AppShell no longer touches it.
  const breadcrumbEntries = [...navRegistry, ...moduleNavEntries(), ...settingsRegistry, ...moduleSettingsEntries()];
  const breadcrumbs = resolveBreadcrumbs(location.pathname, breadcrumbEntries, caps);

  return (
    // `data-print="hide"` — the report print stylesheet's real hook
    // (`reportPrint.css`'s `body.report-print [data-print='hide']` rule).
    // Real bug, found live (2026-08-22): that stylesheet's OWN comment
    // claimed `header.app-header` as this element's selector, but this
    // header has never carried an `app-header` class — the rule matched
    // nothing, so the whole top bar (search, theme toggle, org switcher)
    // printed along with the report. The escape hatch it defines for
    // exactly this ("marked at the call site rather than guessed at from
    // element types") was simply never applied here.
    //
    // Story 2-132: header restyled onto 2-136's flat visual language
    // (bg-card + border-border, the "surface"/"hairline" roles) — the
    // `.glass`/`shadow-glass` treatment this used to carry is explicitly
    // NOT reintroduced as a default (2-136 retired that; expressive glass
    // stays reserved for primary action/status/active-AI surfaces). Avatar,
    // identity and sign-out moved OUT entirely (now the sidebar's account
    // block footer); the wide fake-input search bar is retired for a real
    // icon control; every remaining control (hamburger / agent-status
    // trigger / search icon / theme toggle) shares `--shell-control-h` via
    // the `h-control`/`w-control` Tailwind utilities (tailwind.config.ts,
    // this story) instead of four hand-rolled heights.
    <header className="sticky top-0 z-30 min-h-header border-b border-border bg-card" data-print="hide">
      <Cluster justify="between" gap={4} className="h-full min-h-header px-4 py-2">
        <Cluster gap={3}>
          <IconButton
            className="border border-border text-foreground sm:hidden"
            onClick={onToggleSidebar}
            aria-label="Toggle navigation"
            icon={<Bars3Icon className="h-5 w-5" />}
          />
          <div className="min-w-0">
            <p className="text-caption uppercase tracking-eyebrow text-muted-foreground">DefectSnap</p>
            {/* Story 2-129: the real, path-aware crumb trail (resolveBreadcrumbs)
                now renders HERE — this was previously computed and rendered a
                second time inside AppShell's <main>, while this exact slot
                showed a static "Inspection Studio" label that never reflected
                the current page (the "currently split" duplication this
                story's done-when calls out). One owner now: TopBar computes
                and renders it; AppShell does neither. Falls back to the old
                static product label on the dashboard root, where
                resolveBreadcrumbs deliberately returns [] (its own comment:
                a "Dashboard" crumb there would just repeat the page's own
                heading) — this slot must never render empty. Not an <h1>
                either way — this is static/dynamic chrome repeated on every
                /app page, and each page below already provides its own real
                <h1> for its actual content (a page must have exactly one).
                font-display/tracking-tight/leading-[1.2] on the fallback
                reproduce the h1/h2/h3 base-layer treatment (index.css) that a
                heading tag gets automatically, since a <p> doesn't. */}
            {breadcrumbs.length > 0 ? (
              <Breadcrumbs items={breadcrumbs} />
            ) : (
              <p className="font-display text-xl font-semibold tracking-tight leading-[1.2] text-foreground">
                Inspection Studio
              </p>
            )}
          </div>
        </Cluster>

        <Cluster gap={2}>
          {aiEnabled && (
            // Story 2-133: the SOLE entry point for the shell's own agent
            // workspace — the floating "Build with AI" launcher AgentPanel
            // used to also render at this mount site is gone (that
            // component now renders `variant="drawer"` here, no launcher of
            // its own). `aria-expanded` reflects the drawer's real
            // open/closed state (AGENT_OPEN_EVENT above) — standard
            // disclosure-button semantics; closing still happens from
            // inside the panel (its own ✕), matching this exact file's
            // OPEN_SEARCH_EVENT trigger just above, which is open-only from
            // here too.
            //
            // Found live (2026-08-24): this used to be `hidden ... sm:flex`
            // — a leftover from when the floating launcher was the
            // mobile-reachable entry point and this button was a desktop
            // nicety only. Now that it's the SOLE entry point, hiding it
            // below `sm` left phone users with NO way to open the agent at
            // all — directly contradicting "exactly one entry point... in
            // every state" (zero is not one). Fixed as ONE always-visible
            // button (never two DOM nodes competing for the same accessible
            // name across breakpoints) that collapses to icon-only below
            // `sm` — same shape the search icon button just below already
            // uses at every width — and reveals the "Agent"/status labels
            // only at `sm:` and up.
            // Story 2-147wa — real `IconButton` (P10 §4.4/G-22) instead of a
            // raw `<button>`: `IconButton`'s `children` slot (story 2-147c —
            // "optional VISIBLE content rendered after the icon", box-class
            // sizing suppressed once `children` is present) is the exact
            // shape this control already needed — icon+status-dot always
            // visible, two text labels revealed at `sm:` and up — so the
            // migration changes the element, not the layout/behavior.
            <AgentStatusTrigger />
          )}

          {/* Search — story 2-132 retires the wide fake-input bar (it never
              accepted typed input itself; it only ever dispatched
              OPEN_SEARCH_EVENT to open the real CommandPalette) for a plain
              icon control carrying the identical behavior and accessible
              name. Story 2-147wa: real `IconButton` instead of a raw
              `<button>` — no `children`, so it keeps its normal `md`
              icon-only box (`h-control w-control`, byte-identical to the
              literal classes this control hand-rolled before). */}
          <IconButton
            onClick={() => window.dispatchEvent(new CustomEvent(OPEN_SEARCH_EVENT))}
            className="border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
            aria-label="Open search (Command or Control + K)"
            icon={<MagnifyingGlassIcon className="h-4 w-4" />}
          />

          <ThemeToggle className="h-control" />

          {/* No notifications feature exists yet (story 2-104: an unfinished
              destination must be hidden, not a bell with a fake unread dot
              that goes nowhere) — removed until a real surface lands.
              Avatar/identity/sign-out moved to the sidebar's account block
              footer (story 2-132) — no longer rendered here at all. */}
        </Cluster>
      </Cluster>
    </header>
  );
}
